
import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Loader2, RefreshCw } from "lucide-react";
import { useToast } from "@/hooks/use-toast";

interface ResumeFormActionsProps {
  isProcessing: boolean;
  isUploading: boolean;
  isFormValid: boolean;
  onOptimize: () => void;
}

const ResumeFormActions = ({
  isProcessing,
  isUploading,
  isFormValid,
  onOptimize,
}: ResumeFormActionsProps) => {
  const [hasError, setHasError] = useState(false);
  const [retryCount, setRetryCount] = useState(0);
  const { toast } = useToast();
  
  const handleOptimize = async () => {
    try {
      setHasError(false);
      await onOptimize();
    } catch (error: any) {
      setHasError(true);
      console.error("Edge function error:", error);
      
      // Provide more detailed error messages based on error type
      if (error.message?.includes('Failed to fetch') || error.message?.includes('Failed to send')) {
        toast({
          title: "Development Mode",
          description: "Edge Functions aren't available in this environment. This is expected during development.",
          variant: "default",
        });
      } else if (error.message?.includes('429')) {
        toast({
          title: "Service Limit Reached",
          description: "Our AI service has reached its request limit. Please try again later.",
          variant: "destructive",
        });
      } else {
        toast({
          title: "Optimization Error",
          description: error.message || "Failed to optimize your resume. Please try again later.",
          variant: "destructive",
        });
      }
    }
  };

  const handleRetry = () => {
    setHasError(false);
    setRetryCount(prev => prev + 1);
    toast({
      title: "Retrying",
      description: "Attempting to reconnect to the optimization service...",
    });
    handleOptimize();
  };

  if (hasError) {
    return (
      <Button 
        onClick={handleRetry} 
        className="w-full bg-amber-500 hover:bg-amber-600"
        disabled={isProcessing || isUploading}
      >
        <RefreshCw className="mr-2 h-4 w-4" />
        Retry Optimization
      </Button>
    );
  }

  return (
    <Button 
      onClick={handleOptimize} 
      className="w-full"
      disabled={isProcessing || isUploading || !isFormValid}
    >
      {isProcessing ? (
        <>
          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
          Optimizing Resume...
        </>
      ) : (
        "Optimize Resume"
      )}
    </Button>
  );
};

export default ResumeFormActions;
